import { forwardRef, memo, useState, type MouseEvent } from 'react';
import type { Target, TargetAndTransition, Transition } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { DATA_ATTR_IS_EXPANDED, dataTableRow, TABLE_ROW_EXPANDING_DURATION_MS } from '../../constants.ts';
import { useTableContext } from '../../context.tsx';
import { SubRowsContainer } from '../SubRow/index.tsx';
import { ExpandableContent, ExpandableMotion, ExpandedOutline, GroupIndicator, RowWrapper, RowInner } from './styled.ts';
import type { RowProps } from './types.ts';

const initial: Target = { height: 0 };
const animate: TargetAndTransition = { height: 'auto' };
const exit: TargetAndTransition = { height: 0 };
const transition: Transition = { duration: TABLE_ROW_EXPANDING_DURATION_MS / 1000, ease: 'easeInOut' };

// Interactive descendants handle their own clicks. The row-level click (expand toggle / onClick) is
// suppressed when the event originates from one of these so a button/link inside the row — including
// inside subRows or expandable content — doesn't both fire and toggle the row.
const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, label, [role="button"], [role="checkbox"], [role="switch"], [role="tab"]';

export const Row = memo(
  forwardRef<HTMLDivElement, RowProps>(function Row(
    {
      children,
      className,
      expandableContent,
      flash,
      highlighted,
      isDragging,
      isExpanded: isExpandedProp,
      onExpandedChange,
      targetRef,
      withCellDividers,
      subRows,
      onClick,
      dataTestId,
    },
    ref,
  ) {
    const { inner, compact } = useTableContext();
    const [internalExpanded, setInternalExpanded] = useState<boolean>(false);
    const isControlled = isExpandedProp !== undefined;
    const isExpanded = isControlled ? isExpandedProp : internalExpanded;
    // A controlled expandable row can only toggle if the parent supplies a handler. Without one it's
    // display-only (e.g. the drag-overlay preview), so don't present it as interactive — otherwise
    // it would show a pointer cursor and swallow clicks that do nothing.
    const canToggleExpansion = !!expandableContent && (!isControlled || !!onExpandedChange);
    const isInteractive = canToggleExpansion || !!onClick;

    const toggleExpandableRow = () => {
      if (isControlled) onExpandedChange?.(!isExpanded);
      else setInternalExpanded((prev) => !prev);
    };

    const handleRowClick = useCallbackRef((event: MouseEvent<HTMLDivElement>) => {
      if (event.target instanceof Element && event.target.closest(INTERACTIVE_SELECTOR)) return;
      if (expandableContent) toggleExpandableRow();
      else onClick?.(event);
    });

    const rowClickHandler = isInteractive ? handleRowClick : undefined;

    return (
      <RowWrapper
        className={className}
        ref={ref}
        role='presentation'
        $inner={inner}
        $isInteractive={isInteractive}
        $flash={flash}
        $highlighted={highlighted || isExpanded}
        $isDragging={isDragging}
        {...dataTableRow}
        {...{ [DATA_ATTR_IS_EXPANDED]: isExpanded }}
        data-testid={dataTestId}>
        <GroupIndicator />
        {expandableContent && <ExpandedOutline />}
        <RowInner
          ref={targetRef}
          $isInteractive={isInteractive}
          $flash={flash}
          $highlighted={highlighted || isExpanded}
          $withCellDividers={withCellDividers}
          onClick={rowClickHandler}
          role='row'>
          {children}
        </RowInner>
        {subRows && (
          <SubRowsContainer $compact={compact} $isInteractive={isInteractive} onClick={rowClickHandler}>
            {subRows}
          </SubRowsContainer>
        )}
        {expandableContent && (
          <AnimatePresence initial={false}>
            {isExpanded && (
              <ExpandableMotion initial={initial} animate={animate} exit={exit} transition={transition}>
                <ExpandableContent>{expandableContent}</ExpandableContent>
              </ExpandableMotion>
            )}
          </AnimatePresence>
        )}
      </RowWrapper>
    );
  }),
);
